// WHY — 4×2 grid of metric tiles (responsive — collapses to 3×3 / 2×4 via CSS;
// B13 handles the breakpoints). Hands each tile a pre-built big-chart node for
// the expanded state.

import { useState } from "react";
import { ExpandedMetricChart } from "./ExpandedMetricChart";
import { MetricTile, MetricDirection } from "./MetricTile";
import { fmtMoney, fmtInt, fmtPct } from "../lib/api";
import { rollingAverage } from "../lib/smoothing";

type MetricKey =
  | "cpl"
  | "leads"
  | "spend"
  | "ctr"
  | "cpm"
  | "conv_rate"
  | "impressions"
  | "clicks";

const SMOOTHING_WINDOW = 7;

interface MetricSeries {
  value_7d: number | null;
  delta_pct: number | null;
  sparkline: Array<{ date: string; value: number | null }>;
  alert_active: boolean;
}

interface Props {
  currency: string;
  metrics: {
    cpl: MetricSeries;
    leads: MetricSeries;
    spend: MetricSeries;
    ctr: MetricSeries;
    cpm: MetricSeries;
    conv_rate: MetricSeries;
    impressions: MetricSeries;
    clicks: MetricSeries;
  };
}

interface TileConfig {
  key: keyof Props["metrics"];
  label: string;
  direction: MetricDirection;
  format: (v: number | null, currency: string) => string;
  // axis formatter for the expanded big chart. Always receives a finite number.
  formatAxis: (v: number, currency: string) => string;
}

const fmtMoneyAxis = (v: number, c: string) => fmtMoney(v, c, 0);
const fmtIntAxis = (v: number) => fmtInt(Math.round(v));
const fmtPctAxis = (v: number) => `${v.toFixed(1)}%`;

const TILE_CONFIG: TileConfig[] = [
  { key: "cpl",         label: "CPL",        direction: "up-is-bad",  format: (v, c) => v !== null ? fmtMoney(v, c) : "—", formatAxis: fmtMoneyAxis },
  { key: "leads",       label: "Leads",      direction: "up-is-good", format: (v)    => v !== null ? fmtInt(v) : "—",      formatAxis: fmtIntAxis   },
  { key: "spend",       label: "Spend",      direction: "up-is-bad",  format: (v, c) => v !== null ? fmtMoney(v, c) : "—", formatAxis: fmtMoneyAxis },
  { key: "ctr",         label: "CTR",        direction: "up-is-good", format: (v)    => fmtPct(v),                          formatAxis: fmtPctAxis   },
  { key: "cpm",         label: "CPM",        direction: "up-is-bad",  format: (v, c) => v !== null ? fmtMoney(v, c) : "—", formatAxis: fmtMoneyAxis },
  { key: "conv_rate",   label: "Conv rate",  direction: "up-is-good", format: (v)    => fmtPct(v),                          formatAxis: fmtPctAxis   },
  { key: "impressions", label: "Impressions",direction: "up-is-good", format: (v)    => v !== null ? fmtInt(v) : "—",      formatAxis: fmtIntAxis   },
  { key: "clicks",      label: "Clicks",     direction: "up-is-good", format: (v)    => v !== null ? fmtInt(v) : "—",      formatAxis: fmtIntAxis   },
];

export function MetricTileGrid({ currency, metrics }: Props) {
  const [expandedKey, setExpandedKey] = useState<MetricKey | null>(null);

  const toggle = (k: MetricKey) =>
    setExpandedKey((prev) => (prev === k ? null : k));

  return (
    <div
      className="metric-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 10,
      }}
    >
      {TILE_CONFIG.map(({ key, label, direction, format, formatAxis }) => {
        const m = metrics[key];
        const sparkValues = m.sparkline.map((p) => p.value);
        const isExpanded = expandedKey === key;

        // Smooth the daily series before drawing the expanded chart so the
        // trend reads as a continuous shape instead of day-by-day noise.
        const smoothed = rollingAverage(sparkValues, SMOOTHING_WINDOW);
        const smoothedPoints = m.sparkline.map((p, i) => ({
          date: p.date,
          value: smoothed[i] ?? null,
        }));

        const expandedChart = (
          <div
            style={{
              background: "rgba(255,255,255,0.03)",
              borderRadius: 4,
              padding: 12,
            }}
          >
            <ExpandedMetricChart
              points={smoothedPoints}
              width={1000}
              height={280}
              formatY={(v) => formatAxis(v, currency)}
            />
            <p
              style={{
                margin: "8px 0 0",
                fontSize: "var(--fs-xs)",
                fontFamily: "var(--font-mono)",
                color: "var(--color-bone-dim)",
                letterSpacing: "var(--letter-label)",
                textTransform: "uppercase",
              }}
            >
              {label} · 7-day rolling average
            </p>
          </div>
        );

        return (
          <MetricTile
            key={key}
            label={label}
            value={format(m.value_7d, currency)}
            deltaPct={m.delta_pct}
            sparkline={sparkValues}
            direction={direction}
            alertActive={m.alert_active}
            expanded={isExpanded}
            onToggle={() => toggle(key)}
            gridColumn={isExpanded ? "1 / -1" : undefined}
            expandedChart={expandedChart}
          />
        );
      })}
    </div>
  );
}
